import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { mockData } from '../mock';
import { toast } from 'sonner';
import { submitFormWithGeoLocation } from '../services/api';

const Popup = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    contactNumber: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      const result = await submitFormWithGeoLocation(formData);
      
      if (result.success) {
        toast.success(result.message || 'Thank you! Your request has been submitted.');
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          countryCode: '+1',
          contactNumber: '',
          message: ''
        });
        
        onClose();
        
        // Redirect to application page with customerId
        if (result.customerId) {
          setTimeout(() => {
            window.location.href = `/thanks/?customerid=${result.customerId}`;
          }, 500);
        }
      } else {
        // Show error message
        toast.error(result.error || 'Failed to submit form. Please try again.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-center text-primary">
            Protect Your Brand Now
          </DialogTitle>
          <p className="text-center text-sm text-gray-500 mt-1">
            Speak with a trademark expert — free consultation, no obligations.
          </p>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              placeholder="Your full name"
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              placeholder="your.email@example.com"
              className="mt-1"
            />
          </div>
          
          <div>
            <Label htmlFor="contact">Contact Number *</Label>
            <div className="flex gap-2 mt-1">
              <select
                value={formData.countryCode}
                onChange={(e) => handleChange('countryCode', e.target.value)}
                className="w-[140px] bg-white border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary"
              >
                {mockData.countryCodes.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code} {item.country}
                  </option>
                ))}
              </select>
              <Input
                id="contact"
                type="tel"
                value={formData.contactNumber}
                onChange={(e) => handleChange('contactNumber', e.target.value)}
                required
                placeholder="123-456-7890"
                className="flex-1 bg-white"
              />
            </div>
          </div>
          
          <div>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              placeholder="Tell us about your trademark needs..."
              className="mt-1 min-h-[100px]"
            />
          </div>
          
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="w-full bg-secondary hover:bg-secondary/90 text-white font-semibold py-6 shadow-lg hover:shadow-secondary/50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : 'Submit Request'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Popup;
